import { useState } from "react";
import { newId, type CanonicalStore, type PreferredService } from "../../core/index.ts";
import { BackRow } from "../forms/Modal.tsx";

const inp: React.CSSProperties = {
  width: "100%", background: "var(--panel2)", border: "1px solid var(--edge)", borderRadius: 10,
  color: "var(--cream)", fontFamily: "var(--mono)", fontSize: 13, padding: "10px 14px", boxSizing: "border-box",
};

export function ServicesView({ store, save, onBack }: {
  store: CanonicalStore; save: (s: CanonicalStore) => void; onBack: () => void;
}) {
  const services = store.services;
  const [editing, setEditing] = useState<PreferredService | null>(null);

  if (editing) {
    return <ServiceEditor
      editing={editing}
      onSave={(svc) => { const others = store.services.filter((s) => s.id !== svc.id); save({ ...store, services: [...others, svc] }); setEditing(null); }}
      onDelete={store.services.some((s) => s.id === editing.id) ? () => { save({ ...store, services: store.services.filter((s) => s.id !== editing.id) }); setEditing(null); } : undefined}
      onClose={() => setEditing(null)}
    />;
  }

  return (
    <div className="container glow">
      <BackRow onClick={onBack} />
      <div className="eyebrow amber" style={{ marginTop: 4 }}>PREFERRED SERVICES</div>
      <div className="headline">Your <span className="accent">shops.</span></div>

      <button className="btn ghost" style={{ fontSize: 11, marginTop: 14 }} onClick={() => setEditing({ id: newId(), name: "", createdAt: Math.floor(Date.now() / 1000) })}>+ ADD SERVICE</button>

      {services.length === 0 && <div className="panel mono muted" style={{ fontSize: 12, marginTop: 12 }}>No services yet. Add a mechanic, tyre shop or dealership.</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
        {services.map((s) => (
          <div key={s.id} className="panel" style={{ cursor: "pointer" }} onClick={() => setEditing(s)}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="mono cream" style={{ fontSize: 14, fontWeight: 700 }}>{s.name}</div>
                {s.category && <div className="mono amber" style={{ fontSize: 9, letterSpacing: 2, marginTop: 2 }}>{s.category.toUpperCase()}</div>}
              </div>
              <span className="mono muted" style={{ fontSize: 14 }}>›</span>
            </div>
            <div className="mono muted" style={{ fontSize: 10, marginTop: 6, display: "flex", flexDirection: "column", gap: 3 }}>
              {s.address && <span>📍 {s.address}</span>}
              {s.phone && <a href={`tel:${s.phone.replace(/[^\d+]/g, "")}`} className="amber" style={{ textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>☎ {s.phone}</a>}
              {s.email && <a href={`mailto:${s.email}`} className="amber" style={{ textDecoration: "none" }} onClick={(e) => e.stopPropagation()}>✉ {s.email}</a>}
              {s.notes && <span>{s.notes}</span>}
            </div>
          </div>
        ))}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}

function ServiceEditor({ editing, onSave, onDelete, onClose }: {
  editing: PreferredService; onSave: (s: PreferredService) => void; onDelete?: () => void; onClose: () => void;
}) {
  const [name, setName] = useState(editing.name ?? "");
  const [category, setCategory] = useState(editing.category ?? "");
  const [address, setAddress] = useState(editing.address ?? "");
  const [phone, setPhone] = useState(editing.phone ?? "");
  const [email, setEmail] = useState(editing.email ?? "");
  const [notes, setNotes] = useState(editing.notes ?? "");
  const field = (label: string, value: string, setter: (v: string) => void, placeholder?: string, type = "text") => (
    <>
      <label className="mono muted" style={{ fontSize: 9, letterSpacing: 1.6 }}>{label}</label>
      <input style={inp} type={type} value={value} placeholder={placeholder} onChange={(e) => setter(e.target.value)} />
    </>
  );
  return (
    <div className="container glow">
      <BackRow onClick={onClose} />
      <div className="screen-title">{editing.name ? "EDIT" : "NEW"} SERVICE</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
        {field("NAME", name, setName, "Joe's Garage")}
        {field("CATEGORY", category, setCategory, "Mechanic · Tyre shop · Dealership")}
        {field("ADDRESS", address, setAddress, "Street, city")}
        {field("PHONE", phone, setPhone, "+1 555 0134", "tel")}
        {field("EMAIL", email, setEmail, "shop@example.com", "email")}
        {field("NOTES", notes, setNotes, "Open Mon–Fri 9–6")}
        <button className="btn primary" style={{ marginTop: 6, opacity: name.trim() ? 1 : 0.5 }} disabled={!name.trim()} onClick={() => onSave({ ...editing, name: name.trim(), category: category.trim() || null, address: address.trim() || null, phone: phone.trim() || null, email: email.trim() || null, notes: notes.trim() || null })}>SAVE</button>
        {onDelete && <button className="btn ghost" style={{ borderColor: "var(--redline)", color: "var(--redline)" }} onClick={onDelete}>DELETE</button>}
      </div>
    </div>
  );
}
